import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, Pane, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Loader2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

import { useUser } from '../../../context/UserContext';
import { useTheme } from '../../../context/ThemeContext';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';
import { useMultiCommodityRegionsData } from '../../../utils/api-hooks/analytics';
import { useWeatherEvents } from '../../../utils/api-hooks/weather';
import { useEventGeometry } from '../../../utils/api-hooks/eventGeometry';

import WeatherMapControls, { WEATHER_LAYERS, EVENT_TYPES, STATUS_OPTIONS } from './WeatherMapControls';
import WeatherMapTimeline from './WeatherMapTimeline';

// Shared map components
import { STATUS_COLORS, OPENWEATHERMAP_API_KEY, API_MODE } from '../map/weatherConstants';
import { CARTO_LIGHT_URL, CARTO_DARK_URL } from '../../../constants/URLs';
import { MapResizeHandler, MapClickHandler } from '../map/MapHelpers';
import AnimatedWeatherLayer from '../map/AnimatedWeatherLayer';
import HazardGeometryLayer, { FitHazardOnReady } from '../map/HazardGeometryLayer';
import HazardCard from '../map/HazardCard';
import HazardEventMarker from '../map/HazardEventMarker';
import { useMapInsets } from '../map/useMapInsets';
import { useHazardSelection } from '../map/useHazardSelection';
import { hazardBounds } from '../../../utils/gdacs/hazardBounds';
import { shiftedStormBounds, regionPinPosition } from '../../../utils/gdacs/regionPinFrame';
import { pathUpdatedMsOf } from '../../../utils/gdacs/geometryQuery';
import { eventListRefetchInterval, shouldRevalidateEvents } from '../../../utils/gdacs/eventListPolicy';
import { hazardBiqSummary } from '../../../utils/gdacs/hazardBiqSummary';
import { resolveStormClass } from '../../../utils/gdacs/stormClass';
import { useBiqPageContext } from '../../../context/BiqPageContext';
import { timelineEpochMs, stormStateAt, staticStormState } from '../../../utils/gdacs/stormClock';
import { RegionPopupContent } from '../map/WeatherPopups';
import WeatherLayerLegend from '../map/WeatherLayerLegend';
import StatusLegend from '../map/StatusLegend';
import { injectLeafletStyles } from '../map/leafletStyles';
import { createLeafletPinIcon } from '../map/leafletPinIcon';
import { getHazardIcon } from '../map/hazardIcon';

/**
 * FitBoundsToRegions - When country/region is selected, fit map bounds to markers; when "All", reset to world view
 */
function FitBoundsToRegions({ markers, selectedCountry, onUserViewChange }) {
  const map = useMap();
  const prevCountryRef = useRef(selectedCountry);
  const isProgrammaticRef = useRef(false);
  const programmaticTimerRef = useRef(null);

  const markProgrammatic = useCallback(() => {
    isProgrammaticRef.current = true;
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    programmaticTimerRef.current = setTimeout(() => {
      isProgrammaticRef.current = false;
    }, 1000);
  }, []);

  useEffect(() => {
    const handleViewChange = () => {
      if (isProgrammaticRef.current) return;
      if (prevCountryRef.current !== '') {
        prevCountryRef.current = '';
        if (onUserViewChange) onUserViewChange();
      }
    };
    map.on('zoomend', handleViewChange);
    map.on('moveend', handleViewChange);
    return () => {
      map.off('zoomend', handleViewChange);
      map.off('moveend', handleViewChange);
      if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current);
    };
  }, [map, onUserViewChange]);

  useEffect(() => {
    if (selectedCountry && markers && markers.length > 0) {
      prevCountryRef.current = selectedCountry;
      const valid = markers.filter(
        (m) => m.lat != null && m.lng != null && !Number.isNaN(m.lat) && !Number.isNaN(m.lng)
      );
      if (valid.length > 0) {
        markProgrammatic();
        const bounds = L.latLngBounds(valid.map((m) => [m.lat, m.lng]));
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 8, animate: true });
      }
      return;
    }
    if (prevCountryRef.current !== '') {
      prevCountryRef.current = '';
      markProgrammatic();
      map.setView([20, 0], 2, { animate: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markers, selectedCountry]);

  return null;
}

// Stable default array identity.
const NO_COMMODITIES = [];

/**
 * WeatherMap - Main weather map component for the Home page
 */
const WeatherMap = ({ commodities = NO_COMMODITIES }) => {
  const { t } = useTranslation();
  const { email } = useUser();
  const { isDark } = useTheme();
  const { fontSize } = useGlobalFontSize();

  // State
  const [activeLayer, setActiveLayer] = useState('temp');
  const [activeEventTypes, setActiveEventTypes] = useState([]);
  const [visibleCommodities, setVisibleCommodities] = useState([...commodities]);
  const [visibleStatuses, setVisibleStatuses] = useState(STATUS_OPTIONS.map(s => s.id));
  const [currentHour, setCurrentHour] = useState(0);
  // Bumped by every user scrub (slider drag / marker click), never by the play interval, so the storm glyph snaps.
  const [scrubRevision, setScrubRevision] = useState(0);
  const handleScrub = useCallback((hour) => {
    setScrubRevision((r) => r + 1);
    setCurrentHour(hour);
  }, []);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPausedForZoom, setIsPausedForZoom] = useState(false);
  const [isTilesLoading, setIsTilesLoading] = useState(false);
  const [isLayerLoading, setIsLayerLoading] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1500);
  // Hour 0 of the timeline is mount time.
  const [startDate] = useState(() => new Date());
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const playIntervalRef = useRef(null);
  const prevCommoditiesRef = useRef(commodities);
  const isPlayingRef = useRef(false);
  const isTilesLoadingRef = useRef(false);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    isTilesLoadingRef.current = isTilesLoading;
  }, [isTilesLoading]);

  // Fetch regions for all selected commodities
  const { regions, isLoading: regionsLoading } = useMultiCommodityRegionsData(
    email, commodities, null
  );

  // Fetch weather events
  // The refresh interval depends on the selected storm, which only exists further down: it goes through state.
  const [eventsRefetchMs, setEventsRefetchMs] = useState(false);
  const {
    events, isLoading: eventsLoading, refetch: refetchEvents, dataUpdatedAt: eventsUpdatedAt,
  } = useWeatherEvents({ refetchInterval: eventsRefetchMs });

  // Update visible commodities when commodities prop changes
  useEffect(() => {
    setVisibleCommodities([...commodities]);
  }, [commodities]);

  // Show updating animation when commodities change
  useEffect(() => {
    if (JSON.stringify(prevCommoditiesRef.current) !== JSON.stringify(commodities)) {
      setIsUpdating(true);
      const timer = setTimeout(() => setIsUpdating(false), 800);
      prevCommoditiesRef.current = commodities;
      return () => clearTimeout(timer);
    }
  }, [commodities]);

  // Inject custom Leaflet styles
  useEffect(() => {
    return injectLeafletStyles(isDark, 'leaflet-custom-styles-weather-map');
  }, [isDark]);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => {
      const next = !prev;
      isPlayingRef.current = next;
      // If user hits play while tiles are still loading (e.g. right after zoom),
      // buffer playback and show loading state on the play button.
      setIsPausedForZoom(next && isTilesLoadingRef.current);
      return next;
    });
  }, []);

  const handleZoomStart = useCallback(() => {
    if (isPlayingRef.current) setIsPausedForZoom(true);
  }, []);

  const handleTilesLoaded = useCallback(() => {
    if (isPlayingRef.current) setIsPausedForZoom(false);
  }, []);

  const handleLayerLoading = useCallback(() => setIsLayerLoading(true), []);
  const handleLayerReady = useCallback(() => setIsLayerLoading(false), []);
  const handleTilesLoadingChange = useCallback((loading) => {
    const next = !!loading;
    setIsTilesLoading(next);
    // While playing, force buffering whenever tiles are loading.
    if (isPlayingRef.current) setIsPausedForZoom(next);
  }, []);

  // Play interval effect
  useEffect(() => {
    // Never advance the timeline while the map tiles for the visible frame are still loading.
    const shouldAnimate = isPlaying && !isPausedForZoom && !isTilesLoading;
    if (shouldAnimate) {
      playIntervalRef.current = setInterval(() => {
        setCurrentHour((prev) => {
          const step = API_MODE === 'paid' ? 3 : 1;
          return (prev + step) % 241;
        });
      }, playSpeed);
    } else {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    }
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    };
  }, [isPlaying, isPausedForZoom, isTilesLoading, playSpeed]);

  const pinRegions = regions;

  // Filter regions
  const filteredRegions = useMemo(() => {
    if (visibleCommodities.length === 0 || visibleStatuses.length === 0) return [];
    return pinRegions.filter(
      (r) =>
        visibleCommodities.includes(r.commodityKey) &&
        visibleStatuses.includes(r.harvest_status) &&
        r.latitude != null && r.longitude != null
    );
  }, [pinRegions, visibleCommodities, visibleStatuses]);

  // Country options
  const countryOptions = useMemo(() => {
    const seen = new Set();
    return filteredRegions
      .filter((r) => { const c = (r.country || '').trim(); if (!c || seen.has(c)) return false; seen.add(c); return true; })
      .map((r) => { const country = (r.country || '').trim(); return { value: country, label: country }; })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredRegions]);

  // Region options
  const regionOptions = useMemo(() => {
    if (!selectedCountry) return [];
    const seen = new Set();
    return filteredRegions
      .filter((r) => (r.country || '').trim() === selectedCountry)
      .filter((r) => { const reg = (r.region || '').trim(); if (!reg || seen.has(reg)) return false; seen.add(reg); return true; })
      .map((r) => { const region = (r.region || '').trim(); return { value: region, label: region }; })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredRegions, selectedCountry]);

  // Markers for bounds
  const markersForBounds = useMemo(() => {
    if (!selectedCountry) return [];
    return filteredRegions
      .filter((r) => {
        if ((r.country || '').trim() !== selectedCountry) return false;
        if (selectedRegion) return (r.region || '').trim() === selectedRegion;
        return true;
      })
      .map((r) => ({ lat: r.latitude, lng: r.longitude }));
  }, [selectedCountry, selectedRegion, filteredRegions]);

  const regionPinIconCache = useMemo(() => new Map(), []);

  const getRegionPinIcon = useCallback((status) => {
    const fillColor = STATUS_COLORS[status] || STATUS_COLORS.not_producing;
    const variant = status === 'not_producing' ? 'glass' : 'solid';
    const key = `region|${fillColor}|${variant}`;
    const cached = regionPinIconCache.get(key);
    if (cached) return cached;
    const icon = createLeafletPinIcon({ fillColor, sizePx: 28, variant });
    regionPinIconCache.set(key, icon);
    return icon;
  }, [regionPinIconCache]);

  const handleCountryChange = useCallback((country) => {
    setSelectedCountry(country);
    setSelectedRegion('');
  }, []);

  const handleUserViewChange = useCallback(() => {
    setSelectedCountry('');
    setSelectedRegion('');
  }, []);

  // Filter events
  const filteredEvents = useMemo(() => {
    if (activeEventTypes.length === 0) return [];
    return events.filter((e) => activeEventTypes.includes(e.type));
  }, [events, activeEventTypes]);

  const handleEventTypeToggle = useCallback((eventType) => {
    setActiveEventTypes((prev) =>
      prev.includes(eventType) ? prev.filter((t) => t !== eventType) : [...prev, eventType]
    );
  }, []);

  // Hazard detail: one selected event, its lazily fetched footprint, the pins inside it and the
  // storm position for the shared timeline clock.
  const { selectedEvent, selectEvent, clearSelection } = useHazardSelection({
    events,
    activeEventTypes,
    enabled: true,
  });
  // Event list: revalidated when a live cyclone is opened (if older than 5 min) and polled while one stays selected.
  useEffect(() => { setEventsRefetchMs(eventListRefetchInterval(selectedEvent)); }, [selectedEvent]);
  useEffect(() => {
    if (shouldRevalidateEvents({ event: selectedEvent, dataUpdatedAt: eventsUpdatedAt, nowMs: Date.now() })) refetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);
  const {
    parsed, status: geometryStatus, meta: pathMetaState, refetch: refetchGeometry
  } = useEventGeometry(selectedEvent);
  const clockMs = useMemo(() => timelineEpochMs(startDate, currentHour), [startDate, currentHour]);
  const pathUpdatedMs = pathUpdatedMsOf(parsed);
  // The selected hazard goes to BiQ as page context (its own slot, so the page's extras are never overwritten).
  const { setPageHazard } = useBiqPageContext() || {};
  const classWords = resolveStormClass({ text: selectedEvent?.severityData?.text, parsed, lng: selectedEvent?.lng });
  useEffect(() => {
    if (!setPageHazard) return undefined;
    setPageHazard(selectedEvent ? hazardBiqSummary({ event: selectedEvent, classWords, status: geometryStatus }) : null);
    return () => setPageHazard(null);
  }, [setPageHazard, selectedEvent, classWords, geometryStatus]);
  const selectedIsCurrent = !!selectedEvent?.isCurrent;
  const clockState = useMemo(() => {
    if (!parsed || parsed.eventType !== 'TC') return null;
    return selectedIsCurrent ? stormStateAt(parsed, clockMs) : staticStormState(parsed);
  }, [parsed, clockMs, selectedIsCurrent]);
  // Free rectangle of the map (outside the storm card) for fits.
  const { insets, setWrapperEl, setPanelEl } = useMapInsets();
  const [fitNonce, setFitNonce] = useState(0);
  const handleSelectHazard = useCallback((e) => {
    selectEvent(e);
    setFitNonce((n) => n + 1);
  }, [selectEvent]);
  const hazardDetail = useMemo(() => (selectedEvent ? {
    parsed, status: geometryStatus, clockState, clockMs, pathUpdatedMs, pathMeta: pathMetaState, onRetry: refetchGeometry,
  } : null), [selectedEvent, parsed, geometryStatus, clockState, clockMs, pathUpdatedMs, pathMetaState, refetchGeometry]);
  const hazardFitBounds = useMemo(() => (parsed ? hazardBounds(parsed) : null), [parsed]);
  const stormPinBounds = useMemo(() => shiftedStormBounds(parsed), [parsed]);
  const hazardLabels = useMemo(() => ({
    shortClasses: {
      HU: t('map.hazard.shortClasses.HU', 'H'),
      TS: t('map.hazard.shortClasses.TS', 'TS'),
      TD: t('map.hazard.shortClasses.TD', 'TD'),
    },
  }), [t]);


  const hasApiKey = !!OPENWEATHERMAP_API_KEY;
  const showLoading = regionsLoading || eventsLoading || isUpdating;

  return (
    <div className="border border-[#C3C4C3] dark:border-[#828382] rounded-lg bg-white dark:bg-[#191919]">
      {/* Title */}
      <div className="px-4 py-3 border-b border-[#E8EBE9] dark:border-[#3a3c3b]">
        <h3 className="font-semibold text-bloom-green" style={{ fontSize: fontSize + 2 }}>
          {t('home.weatherMap.title', 'Growing Regions & Weather')}
        </h3>
      </div>

      {/* Controls */}
      <WeatherMapControls
        commodities={commodities}
        regions={regions}
        visibleCommodities={visibleCommodities}
        visibleStatuses={visibleStatuses}
        onCommoditiesChange={setVisibleCommodities}
        onStatusesChange={setVisibleStatuses}
        countryOptions={countryOptions}
        selectedCountry={selectedCountry}
        onCountryChange={handleCountryChange}
        regionOptions={regionOptions}
        selectedRegion={selectedRegion}
        onRegionChange={setSelectedRegion}
        activeLayer={activeLayer}
        onLayerChange={setActiveLayer}
        activeEventTypes={activeEventTypes}
        onEventTypeToggle={handleEventTypeToggle}
      />

      {/* Map Container */}
      <div ref={setWrapperEl} className="weather-map-container relative z-0 h-[350px] sm:h-[450px] lg:h-[550px]">
        {showLoading && (
          <div
            role="status"
            aria-busy="true"
            aria-label="Loading"
            className="skeleton-sheen !absolute inset-0 z-10 rounded-[8px]"
          />
        )}

        {commodities.length === 0 && !regionsLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="text-center text-[#828382] p-4">
              <p style={{ fontSize }}>{t('home.growingRegions.placeholder', 'No commodities selected yet')}</p>
              <p style={{ fontSize: fontSize - 2 }} className="mt-2">
                {t('home.growingRegions.message', 'Your growing regions will appear here')}
              </p>
            </div>
          </div>
        )}

        {!hasApiKey && (
          <div className="absolute top-2 right-2 z-20 px-2 py-1 rounded bg-[#ff6b6b]/20 text-[#ff6b6b]" style={{ fontSize: fontSize - 3 }}>
            Weather layer: API key not configured
          </div>
        )}

        {isLayerLoading && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-bloom-green/90 text-white flex items-center gap-2 shadow-lg" style={{ fontSize: fontSize - 2 }}>
            <Loader2 size={14} className="animate-spin" />
            <span>Loading {WEATHER_LAYERS.find(l => l.id === activeLayer)?.label || 'layer'}...</span>
          </div>
        )}

        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ height: '100%', width: '100%', background: isDark ? '#1d1d1d' : '#f3f4f6' }}
          zoomControl={true}
          scrollWheelZoom={false}
          key={isDark ? 'dark' : 'light'}
        >
          <MapResizeHandler />
          <FitHazardOnReady bounds={hazardFitBounds} ready={geometryStatus === 'ready'} selectedId={selectedEvent?.id ?? null} fitNonce={fitNonce} insets={insets} />
          <MapClickHandler onMapClick={clearSelection} />
          <FitBoundsToRegions
            markers={markersForBounds}
            selectedCountry={selectedCountry}
            onUserViewChange={handleUserViewChange}
          />

          <TileLayer
            url={isDark ? CARTO_DARK_URL : CARTO_LIGHT_URL}
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />

          {hasApiKey && (
            <AnimatedWeatherLayer
              activeLayer={activeLayer}
              currentHour={currentHour}
              startDate={startDate}
              apiMode={API_MODE}
              isDark={isDark}
              isPlaying={isPlaying}
              onZoomStart={handleZoomStart}
              onTilesLoaded={handleTilesLoaded}
              onLayerLoading={handleLayerLoading}
              onLayerReady={handleLayerReady}
              onTilesLoadingChange={handleTilesLoadingChange}
            />
          )}

          <HazardGeometryLayer
            parsed={parsed}
            clockState={clockState}
            clockMs={clockMs}
            isPlaying={isPlaying}
            playSpeed={playSpeed}
            isDark={isDark}
            isCurrent={selectedIsCurrent}
            scrubRevision={scrubRevision}
            labels={hazardLabels}
          />

          {/* Region markers */}
          {filteredRegions.map((region, idx) => (
            <Marker
              key={`region-${region.commodityKey}-${region.specific_location || region.region}-${idx}`}
              position={regionPinPosition(region, stormPinBounds)}
              icon={getRegionPinIcon(region.harvest_status)}
              eventHandlers={{
                click: (e) => e.target.openPopup(),
              }}
            >
              <Popup>
                <RegionPopupContent region={region} isDark={isDark} />
              </Popup>
            </Marker>
          ))}

          {/* Weather event markers */}
          <Pane name="hazards" style={{ zIndex: 695 }}>
            {filteredEvents.map((event, idx) => {
              const icon = getHazardIcon(event.type);
              if (!icon) return null;
              return (
                <HazardEventMarker
                  key={`event-${event.id || idx}`}
                  event={event}
                  icon={icon}
                  title={t(`map.events.${event.type}`, event.type)}
                  onSelect={handleSelectHazard}
                />
              );
            })}
          </Pane>
        </MapContainer>

        {selectedEvent && (
          <HazardCard
            event={selectedEvent}
            isDark={isDark}
            detail={hazardDetail}
            onClose={clearSelection}
            rootRef={setPanelEl}
          />
        )}

      </div>

      {/* Timeline */}
      <WeatherMapTimeline
        currentHour={currentHour}
        isPlaying={isPlaying}
        isLoadingTiles={isPausedForZoom || isTilesLoading}
        playSpeed={playSpeed}
        onPlayPause={handlePlayPause}
        onTimeChange={handleScrub}
        onSpeedChange={setPlaySpeed}
        startDate={startDate}
        apiMode={API_MODE}
      />

      {/* Weather layer legend */}
      {hasApiKey && (
        <WeatherLayerLegend activeLayer={activeLayer} isDark={isDark} />
      )}

      {/* Status legend */}
      <StatusLegend activeEventTypes={activeEventTypes} />
    </div>
  );
};

export default WeatherMap;
